import React from "react";
import { Link } from "react-router-dom";
import {
  Leaf,
  Sprout,
  Wind,
  Moon,
  Droplets,
  Flower2,
  Waves,
  TreePalm,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { PageShell, PillarSection } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";

// ============================================================================
// Les 3 piliers de l'Harmonisation Végétale
// ============================================================================

const PILLARS = [
  {
    key: "ionic",
    accent: "cyan", // #00F0FF
    overline: { fr: "A — Vitalité & Équilibre Ionique", en: "A — Vitality & Ionic Balance" },
    title: { fr: "Zones Tech & Béton", en: "Tech & Concrete Zones" },
    plants: [
      {
        Icon: Wind,
        photo: "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/oxz2ppo5_foug%C3%A8re%20de%20boston.jpg",
        nameFr: "Fougère de Boston",
        nameEn: "Boston Fern",
        latin: "Nephrolepis exaltata",
        usageFr:
          "La championne pour compenser l'air sec et ionisé positivement par le béton armé. Elle rejette massivement des ions négatifs.",
        usageEn:
          "The champion to offset air dried and positively-ionised by reinforced concrete. It massively releases negative ions.",
      },
      {
        Icon: Sprout,
        photo: "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/a1evgwvg_cactus.jpg",
        nameFr: "Cactus Cereus",
        nameEn: "Cereus Cactus",
        latin: "Cereus peruvianus",
        usageFr:
          "À placer près des postes de travail pour restaurer la vitalité biologique de l'air saturé par les écrans.",
        usageEn:
          "Place near workstations to restore the biological vitality of air saturated by screens.",
      },
      {
        Icon: Moon,
        photo: "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/6cwck6lu_Sansevieria%20%28ou%20Langue%20de%20belle-m%C3%A8re%29.jpg",
        nameFr: "Sansevieria",
        nameEn: "Sansevieria",
        latin: "Dracaena trifasciata · Langue de belle-mère",
        usageFr:
          "« Bio-Hacking Sommeil ». Produit de l'oxygène la nuit pour purifier la chambre sans perturber le repos.",
        usageEn:
          '"Sleep Bio-Hacking". Releases oxygen at night to purify the bedroom without disturbing rest.',
      },
    ],
  },
  {
    key: "detox",
    accent: "green", // emerald
    overline: { fr: "B — Détox & Pureté", en: "B — Detox & Purity" },
    title: { fr: "Pollution chimique", en: "Chemical pollution" },
    plants: [
      {
        Icon: Flower2,
        photo: "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/09fhtp24_Spathiphyllum%20%28ou%20Fleur%20de%20lune%29.jpg",
        nameFr: "Fleur de Lune",
        nameEn: "Peace Lily",
        latin: "Spathiphyllum",
        usageFr:
          "À installer après une rénovation ou l'achat de nouveaux meubles pour absorber les colles et vernis (formaldéhyde).",
        usageEn:
          "To install after renovation or new-furniture delivery to absorb glues and varnishes (formaldehyde).",
      },
      {
        Icon: Droplets,
        photo: "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/wkr0q1np_Chlorophytum%20%28ou%20Plante%20araign%C3%A9e%29.jpg",
        nameFr: "Chlorophytum",
        nameEn: "Spider Plant",
        latin: "Chlorophytum comosum · Plante araignée",
        usageFr:
          "Idéal pour les cuisines ou bureaux exigus. Absorbe jusqu'à 90 % du monoxyde de carbone en 24 h.",
        usageEn:
          "Ideal for compact kitchens or offices. Absorbs up to 90 % of carbon monoxide within 24 h.",
      },
    ],
  },
  {
    key: "flow",
    accent: "gold", // #D4AF37
    overline: { fr: "C — Fluidité & Formes", en: "C — Flow & Shapes" },
    title: { fr: "Géobiologie appliquée", en: "Applied Geobiology" },
    plants: [
      {
        Icon: Waves,
        photo: "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/4mhuvrby_Pothos%20%28ou%20Scindapsus%29.jpg",
        nameFr: "Pothos · Lierre tombant",
        nameEn: "Pothos · Trailing Ivy",
        latin: "Epipremnum aureum · Hedera helix",
        usageFr:
          "À suspendre devant les angles saillants, piliers ou poutres. Action : brise la linéarité agressive des « flèches » pour recréer une onde de forme fluide et protectrice dans la pièce.",
        usageEn:
          "Hang in front of sharp corners, pillars or beams. Action: breaks the aggressive linearity of energetic 'arrows' to recreate a fluid, protective shape-wave within the room.",
      },
    ],
  },
];

// Style helpers — chaque pilier porte sa propre signature couleur
const ACCENT = {
  cyan: {
    border: "border-[#00F0FF]/30",
    borderHover: "hover:border-[#00F0FF]/70",
    iconBg: "bg-[#00F0FF]/10 border-[#00F0FF]/40",
    iconColor: "text-[#00F0FF]",
    overline: "text-[#00F0FF]",
    glow: "from-[#00F0FF]/8",
  },
  green: {
    border: "border-emerald-400/30",
    borderHover: "hover:border-emerald-300/70",
    iconBg: "bg-emerald-400/10 border-emerald-400/40",
    iconColor: "text-emerald-300",
    overline: "text-emerald-300",
    glow: "from-emerald-400/8",
  },
  gold: {
    border: "border-[#D4AF37]/30",
    borderHover: "hover:border-[#D4AF37]/70",
    iconBg: "bg-[#D4AF37]/10 border-[#D4AF37]/40",
    iconColor: "text-[#D4AF37]",
    overline: "text-[#D4AF37]",
    glow: "from-[#D4AF37]/8",
  },
};

const PlantCard = ({ plant, accent, isFr, testId }) => {
  const s = ACCENT[accent];
  const { Icon } = plant;
  const hasPhoto = Boolean(plant.photo);
  return (
    <div
      className={`group relative rounded-2xl border ${s.border} ${s.borderHover} bg-[#0A1128]/60 hover:bg-[#0A1128] transition-colors h-full overflow-hidden flex flex-col`}
      data-testid={testId}
    >
      {hasPhoto ? (
        <div className="relative aspect-[4/3] overflow-hidden bg-[#0F1A3A]">
          <img
            src={plant.photo}
            alt={isFr ? plant.nameFr : plant.nameEn}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F1A3A] via-[#0F1A3A]/30 to-transparent" />
          <div className={`absolute top-3 left-3 inline-flex items-center justify-center w-10 h-10 rounded-xl border backdrop-blur-md ${s.iconBg}`}>
            <Icon size={18} className={s.iconColor} strokeWidth={1.5} />
          </div>
        </div>
      ) : (
        <div
          className={`absolute -top-20 -right-20 w-48 h-48 rounded-full bg-gradient-radial ${s.glow} to-transparent opacity-60 pointer-events-none`}
        />
      )}
      <div className="relative p-6 flex-1">
        {!hasPhoto && (
          <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl border ${s.iconBg} mb-5`}>
            <Icon size={22} className={s.iconColor} strokeWidth={1.5} />
          </div>
        )}
        <h3 className="font-display text-xl text-amber-glow leading-tight mb-1">
          {isFr ? plant.nameFr : plant.nameEn}
        </h3>
        <p className={`text-[11px] font-mono-tech uppercase tracking-[0.22em] ${s.overline} mb-4 italic`}>
          {plant.latin}
        </p>
        <p className="label-overline mb-2 text-slate-500">
          {isFr ? "— Usage Expert" : "— Expert Use"}
        </p>
        <p className="text-sm text-slate-300 leading-relaxed">
          {isFr ? plant.usageFr : plant.usageEn}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// Page
// ============================================================================

export default function SentinellesVertes() {
  const { locale } = useLang();
  const isFr = locale === "fr";

  return (
    <PageShell
      testId="page-sentinelles-vertes"
      title={isFr ? "Les Sentinelles Vertes" : "The Green Sentinels"}
      subtitle={
        isFr
          ? "Le végétal comme outil technologique — biocapteur et équilibreur d'ions, complément indispensable de la géobiologie FACOW360."
          : "Plants as a technological tool — bio-sensors and ion-balancers, an indispensable complement to FACOW360 geobiology."
      }
    >
      {/* Accroche de l'Expert */}
      <Reveal direction="up" amount={0.2}>
        <PillarSection
          heading={isFr ? "L'accroche de l'expert" : "The expert's stance"}
          testId="sv-section-intro"
        >
          <p>
            {isFr
              ? "La plante ne bloque pas l'onde, elle soutient la vie là où l'environnement s'est appauvri. Mon protocole intègre le végétal comme un filtre biologique indispensable pour restaurer la vitalité de vos espaces."
              : "The plant does not block the wave; it supports life where the environment has been depleted. My protocol integrates the plant as an indispensable biological filter to restore the vitality of your spaces."}
          </p>
          <p>
            {isFr
              ? "Loin du simple ornement, chaque espèce listée ci-dessous a été choisie pour sa fonction précise : ionisation, détoxication ou correction d'onde de forme. C'est de la géobiologie appliquée, version vivante."
              : "Far from a mere ornament, each species listed below was chosen for a specific function: ionisation, detoxification or correction of shape-waves. This is applied geobiology — the living version."}
          </p>
        </PillarSection>
      </Reveal>

      {/* Les 3 piliers */}
      <div className="mt-12 space-y-14" data-testid="sv-pillars">
        {PILLARS.map((p, pi) => (
          <Reveal key={p.key} direction="up" amount={0.15} delay={pi * 0.05}>
            <section data-testid={`sv-pillar-${p.key}`}>
              <p className={`label-overline mb-3 ${ACCENT[p.accent].overline}`}>
                {isFr ? p.overline.fr : p.overline.en}
              </p>
              <h2 className="font-display text-2xl md:text-3xl text-amber-glow leading-tight tracking-tight mb-7">
                {isFr ? p.title.fr : p.title.en}
              </h2>
              <div
                className={`grid grid-cols-1 ${
                  p.plants.length === 1
                    ? "md:grid-cols-1 max-w-3xl"
                    : p.plants.length === 2
                    ? "md:grid-cols-2"
                    : "md:grid-cols-2 lg:grid-cols-3"
                } gap-5`}
              >
                {p.plants.map((plant, i) => (
                  <Reveal key={i} direction="up" delay={i * 0.08} duration={0.7} amount={0.1}>
                    <PlantCard
                      plant={plant}
                      accent={p.accent}
                      isFr={isFr}
                      testId={`sv-plant-${p.key}-${i}`}
                    />
                  </Reveal>
                ))}
              </div>
            </section>
          </Reveal>
        ))}
      </div>

      {/* Le Conseil FACOW (pull-quote) */}
      <Reveal direction="up" amount={0.2}>
        <blockquote
          className="mt-16 relative rounded-2xl border border-emerald-400/40 bg-gradient-to-br from-emerald-400/10 via-[#0A1128]/60 to-[#D4AF37]/5 px-8 py-10 md:px-12 md:py-14"
          data-testid="sv-conseil-facow"
        >
          <div className="absolute top-5 left-6 inline-flex items-center justify-center w-12 h-12 rounded-full border border-emerald-400/40 bg-emerald-400/10">
            <Leaf size={20} className="text-emerald-300" strokeWidth={1.5} />
          </div>
          <p className="label-overline mb-3 text-emerald-300 ml-16">
            {isFr ? "— Le Conseil FACOW" : "— The FACOW Counsel"}
          </p>
          <p className="relative font-display text-xl md:text-2xl lg:text-3xl text-slate-100 leading-snug tracking-tight max-w-3xl">
            {isFr
              ? "Une plante en bonne santé vibre plus haut. Honorer vos plantes, c'est entretenir le moteur de purification de votre habitat. C'est un pacte d'attention mutuelle entre vous et le vivant."
              : "A healthy plant vibrates higher. Honouring your plants means maintaining the purification engine of your habitat. It is a pact of mutual attention between you and the living."}
          </p>
          <footer className="mt-6 text-[11px] font-mono-tech uppercase tracking-[0.3em] text-[#D4AF37]">
            — Fabrice Marini · FACOW360
          </footer>
        </blockquote>
      </Reveal>

      {/* CTA Géobiologie */}
      <div className="mt-12 flex flex-wrap gap-3">
        <Link to="/geobiologie" className="btn-primary" data-testid="sv-cta-geobio">
          {isFr ? "← Revenir à la Géobiologie" : "← Back to Geobiology"}
        </Link>
        <Link to="/devis" className="btn-gold" data-testid="sv-cta-devis">
          {isFr ? "Demander un devis" : "Request a quote"}
        </Link>
      </div>

      {/* Clause de responsabilité */}
      <div
        className="mt-12 rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-6 py-5"
        data-testid="sv-responsibility"
      >
        <p className="label-overline mb-2 text-[#D4AF37]">
          {isFr ? "— Clause de responsabilité" : "— Disclaimer"}
        </p>
        <p className="text-sm text-slate-300 leading-relaxed italic">
          {isFr
            ? "En tant que conseiller, mon accompagnement vibratoire repose sur une expertise technique et sensitive. Il ne constitue en aucun cas un avis médical ou un diagnostic de santé. Mes services visent l'équilibre de l'habitat et le bien-être général."
            : "As a consultant, my vibrational accompaniment rests on technical and sensitive expertise. It does not constitute medical advice or a health diagnosis under any circumstances. My services aim at habitat balance and general well-being."}
        </p>
      </div>
    </PageShell>
  );
}
